import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { businessInfo as fallbackInfo } from '../data/mock';
import { useToast } from '../components/Toast';
import axios from 'axios';

const ContactPage = () => {
  const toast = useToast();
  const [biz, setBiz] = useState(fallbackInfo);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', vehicle: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBiz = async () => {
      try {
        const res = await axios.get('/api/settings/business');
        setBiz(res.data);
      } catch { /* fallback to mock */ }
    };
    fetchBiz();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, preferred_contact: 'email' }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', service: '', vehicle: '', message: '' });
        }, 5000);
      } else { throw new Error('Failed'); }
    } catch {
      toast.error('There was an error submitting your form. Please try again or call us directly.');
    } finally { setIsLoading(false); }
  };

  const address = biz.address || {};
  const hours = biz.hours || {};

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4 sm:space-y-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
            Contact <span className="text-red-600">{biz.name || 'Texas Tint Studio'}</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to enhance your vehicle or home with professional window tinting?
            Get in touch for a free estimate and expert consultation.
          </p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mb-12 sm:mb-16">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Call Us</h3>
                  <a href={`tel:${biz.phone}`} className="text-red-600 hover:text-red-700 font-medium text-sm sm:text-base">{biz.phone}</a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full">
                  <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Email Us</h3>
                  <a href={`mailto:${biz.email}`} className="text-red-600 hover:text-red-700 font-medium text-xs sm:text-base break-all">{biz.email}</a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Visit Us</h3>
                  <div className="text-gray-600 text-xs sm:text-sm">
                    <p>{address.street}</p>
                    <p>{address.suite}</p>
                    <p>{address.city}, {address.state} {address.zip}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6 text-center space-y-3">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Hours</h3>
                  <div className="text-gray-600 text-xs sm:text-sm space-y-0.5">
                    <p>{hours.weekdays}</p>
                    <p>{hours.saturday}</p>
                    <p>{hours.sunday}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map + Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Form */}
            <Card className="border-0 shadow-xl order-2 lg:order-1">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 text-center">Get Your Free Quote</CardTitle>
                <p className="text-gray-600 text-center text-sm sm:text-base">Fill out the form and we'll get back to you within 24 hours.</p>
              </CardHeader>
              <CardContent>
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Your full name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="(713) 123-4567" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="your@email.com" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Type *</Label>
                      <Select onValueChange={handleSelectChange} required>
                        <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="automotive">Automotive Window Tinting</SelectItem>
                          <SelectItem value="residential">Residential Window Tinting</SelectItem>
                          <SelectItem value="commercial">Commercial Window Tinting</SelectItem>
                          <SelectItem value="consultation">Consultation Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.service === 'automotive' && (
                      <div className="space-y-2">
                        <Label htmlFor="vehicle">Vehicle Information</Label>
                        <Input id="vehicle" name="vehicle" value={formData.vehicle} onChange={handleInputChange} placeholder="Year, Make, Model" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Details</Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows={4} placeholder="Tell us about your project..." />
                    </div>
                    <Button type="submit" disabled={isLoading} className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-3 text-lg rounded-lg">
                      <Send className="h-4 w-4 mr-2" />
                      {isLoading ? 'Sending...' : 'Send My Request'}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center space-y-4 py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Request Sent Successfully!</h3>
                    <p className="text-gray-600">We'll get back to you within 24 hours.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map + Info */}
            <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className="aspect-video sm:aspect-[4/3]">
                  <iframe
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(`${address.street} ${address.suite} ${address.city} ${address.state} ${address.zip}`)}`}
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                    title={`${biz.name} Location`}
                  ></iframe>
                </div>
              </Card>

              {biz.serviceAreas && biz.serviceAreas.length > 0 && (
                <Card className="border-0 shadow-xl">
                  <CardHeader><CardTitle className="text-lg sm:text-xl font-bold text-gray-900">Service Areas</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {biz.serviceAreas.map((area, i) => (
                        <div key={i} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{area}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${address.street} ${address.suite} ${address.city} ${address.state} ${address.zip}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="block w-full bg-gray-900 hover:bg-gray-800 text-white text-center py-3 rounded-lg font-medium transition-colors">
                <MapPin className="h-4 w-4 inline mr-2" />Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
